import React, { useMemo } from 'react';
import ManCrossPageFooter from '../components/ManCrossPageFooter';

// ── Configuración de perfiles ─────────────────────────────────────────────────

// Alineado con SELFCARE_DIMENSION_DEFS: D1→Fatigado, D2→Impulsivo, D3→Distraído,
// D4→Silencioso, D5→Indisciplinado, D6→Aislado, D7→Desconectado.
const PROFILE_COLOR = {
    'Fatigado':       '#E74C3C',
    'Impulsivo':      '#E67E22',
    'Distraído':      '#F0AC68',
    'Silencioso':     '#9AA2AE',
    'Indisciplinado': '#E67E22',
    'Aislado':        '#7B90A8',
    'Desconectado':   '#B0BEC5',
};

// Reglas de grupo: suma de porcentajes > 40 % activa el grupo
const GROUP_DEFS = [
    { key: 'GA', label: 'GA', subtitle: 'Riesgo Físico/Conductual',  profiles: ['Fatigado', 'Indisciplinado'] },
    { key: 'GB', label: 'GB', subtitle: 'Riesgo Cognitivo/Emocional', profiles: ['Distraído', 'Impulsivo'] },
    { key: 'GC', label: 'GC', subtitle: 'Riesgo Social/Cultural',     profiles: ['Silencioso', 'Aislado', 'Desconectado'] },
];
const GROUP_CONFIG = Object.fromEntries(GROUP_DEFS.map(g => [g.key, g]));
GROUP_CONFIG.MIXTO = { key: 'MIXTO', label: 'MIXTO', subtitle: 'Plan Combinado' };

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Badge IVC derivado del promedio IASH del área (semáforo: 50 / 65 / 80) */
const getIvcBadge = (avg) => {
    if (avg == null || !Number.isFinite(avg)) return { label: 'Sin dato', bg: '#9AA2AE', text: '#fff' };
    if (avg < 50)  return { label: 'Riesgo Crítico',  bg: '#E74C3C', text: '#fff'    };
    if (avg < 65)  return { label: 'Riesgo Alto',     bg: '#E67E22', text: '#fff'    };
    if (avg < 80)  return { label: 'Riesgo Moderado', bg: '#D4AC0D', text: '#574400' };
    return              { label: 'Riesgo Bajo',     bg: '#62C190', text: '#fff'    };
};

/** Asigna grupo al área según regla > 40 % */
const getAreaGroup = (profiles, total) => {
    if (!total) return 'MIXTO';
    for (const g of GROUP_DEFS) {
        const sum = g.profiles.reduce((s, n) => s + (profiles[n] || 0), 0);
        if (sum / total * 100 > 40) return g.key;
    }
    return 'MIXTO';
};

/** Callout dinámico basado en el grupo más frecuente entre todas las áreas */
const buildPageCallout = (areaRows) => {
    if (!areaRows.length) return 'El tipo de riesgo conductual determina el plan de intervención — el mismo nivel de riesgo puede requerir estrategias completamente distintas.';
    const counts = {};
    areaRows.forEach(({ groupKey }) => { counts[groupKey] = (counts[groupKey] || 0) + 1; });
    const [topGroup, topCount] = Object.entries(counts).sort(([, a], [, b]) => b - a)[0];
    const total = areaRows.length;
    const cfg = GROUP_CONFIG[topGroup];
    if (topGroup === 'MIXTO') {
        return `Sin un grupo dominante claro — los planes de intervención deben personalizarse por área.`;
    }
    return `${topCount} de ${total} áreas presentan ${cfg.label} — ${cfg.subtitle} como eje central de intervención.`;
};

// ── AreaCard ──────────────────────────────────────────────────────────────────

const AreaCard = ({ areaName, profilesData, areaAvg }) => {
    const profiles = profilesData?.profiles || {};
    const total    = profilesData?.total    || 0;

    const ivc      = getIvcBadge(areaAvg);
    const groupKey = getAreaGroup(profiles, total);
    const groupCfg = GROUP_CONFIG[groupKey];

    // Top 2 perfiles (excluyendo "Sin Perfil") + "Otros" agrupados
    const sorted = Object.entries(profiles)
        .filter(([name]) => name !== 'Sin Perfil')
        .sort(([, a], [, b]) => b - a);
    const top2 = sorted.slice(0, 2);
    const othersCount = sorted.slice(2).reduce((s, [, c]) => s + c, 0)
        + (profiles['Sin Perfil'] || 0);

    const barRows = [
        ...top2.map(([name, count]) => ({
            name,
            pct:   total ? Math.round(count / total * 100) : 0,
            color: PROFILE_COLOR[name] || '#9AA2AE',
        })),
        ...(othersCount > 0 ? [{
            name:  'Otros',
            pct:   total ? Math.round(othersCount / total * 100) : 0,
            color: '#C9D2DB',
        }] : []),
    ];

    return (
        <div className="mgcr-pf-card">

            {/* Header: nombre de área + badge IVC */}
            <div className="mgcr-pf-card-header">
                <span className="mgcr-pf-area-name">{areaName}</span>
                <span className="mgcr-pf-ivc-badge" style={{ background: ivc.bg, color: ivc.text }}>
                    {ivc.label}
                </span>
            </div>

            {/* Barras de distribución de perfiles */}
            <div className="mgcr-pf-bars">
                {barRows.map((row) => (
                    <div key={row.name} className="mgcr-pf-bar-row">
                        <span className="mgcr-pf-bar-label">{row.name}</span>
                        <div className="mgcr-pf-bar-track">
                            <div
                                className="mgcr-pf-bar-fill"
                                style={{ width: `${row.pct}%`, background: row.color }}
                            />
                        </div>
                        <span
                            className="mgcr-pf-bar-pct"
                            style={{ color: row.color === '#C9D2DB' ? '#617993' : row.color }}
                        >
                            {row.pct}%
                        </span>
                    </div>
                ))}
            </div>

            {/* Footer: grupo dominante */}
            <div className="mgcr-pf-card-footer">
                <span className="mgcr-pf-group-badge">{groupCfg.label}</span>
                <span className="mgcr-pf-group-subtitle">{groupCfg.subtitle}</span>
            </div>

        </div>
    );
};

// ── Componente principal ───────────────────────────────────────────────────────

const ManCrossPageProfilesIASH = ({ reportData, headerText, pageNumber }) => {
    const payload     = reportData?.payload || {};
    const iashTotals  = payload?.totals?.iash || {};
    const profilesMap = payload?.iash_profiles_by_area || {};

    // Mapa rápido: areaName → average (para el badge IVC)
    const avgByArea = useMemo(() => {
        const areas = (iashTotals.average_by_management_area || []);
        return Object.fromEntries(areas.map(a => [a.name, a.average]));
    }, [iashTotals]);

    // Ordenar áreas por promedio ASC (peor primero), máx 6
    const areaRows = useMemo(() => {
        const areaNames = Object.keys(profilesMap);
        return areaNames
            .map(name => ({
                name,
                avg:         avgByArea[name] ?? null,
                profilesData: profilesMap[name],
                groupKey:    getAreaGroup(profilesMap[name]?.profiles || {}, profilesMap[name]?.total || 0),
            }))
            .sort((a, b) => (a.avg ?? 999) - (b.avg ?? 999))
            .slice(0, 6);
    }, [profilesMap, avgByArea]);

    const callout = useMemo(() => buildPageCallout(areaRows), [areaRows]);

    return (
        <div className="mgcr-pf-page">

            {/* Barra de encabezado de página */}
            <div className="mgcr-page-header">
                <span className="mgcr-page-header-text">{headerText}</span>
                <span className="mgcr-page-header-num">{pageNumber}</span>
            </div>

            <div className="keep-blankspace" />

            {/* Título */}
            <div className="mgcr-dx-title-area">
                <h2 className="mgcr-dx-main-title">Perfiles Dominantes por Área</h2>
                <p  className="mgcr-dx-subtitle">Riesgo Conductual</p>
            </div>

            {/* Panel contenedor (fondo gris — patrón del informe) */}
            <div className="mgcr-pf-panel">

                {/* Callout dinámico */}
                <div className="mgcr-pf-callout">
                    {callout}
                </div>

                {/* Grid 3×2 de tarjetas */}
                <div className="mgcr-pf-grid">
                    {areaRows.map((row) => (
                        <AreaCard
                            key={row.name}
                            areaName={row.name}
                            profilesData={row.profilesData}
                            areaAvg={row.avg}
                        />
                    ))}
                </div>

            </div>

            <div className="keep-blankspace" />

            <ManCrossPageFooter />

        </div>
    );
};

export default ManCrossPageProfilesIASH;
